import type {
  Condition,
  Operator,
  SignalContext,
  TriggerNode,
} from "@tracking-web/shared";

/**
 * Intérprete diminuto del árbol de condiciones. Sin eval. Lee las señales del
 * contexto y devuelve si el trigger se cumple.
 */

const SCALAR = new Set([
  "scrollDepthPct",
  "readingProgressPct",
  "timeOnPageMs",
  "activeReadingMs",
  "idleMs",
  "exitIntent",
  "backExitIntent",
  "visitCount",
  "device",
  "referrer",
  "quizCompleted",
  "pageUrl",
  "country",
]);

function readSignal(ctx: SignalContext, c: Condition): unknown {
  if (SCALAR.has(c.signal)) {
    return (ctx as Record<string, unknown>)[c.signal];
  }
  // Señal indexada: necesita key.
  const map =
    ((ctx as unknown as Record<string, Record<string, unknown>>)[c.signal] as
      | Record<string, unknown>
      | undefined) || {};
  return c.key != null ? map[c.key] : undefined;
}

function compare(actual: unknown, op: Operator, expected: string | number | boolean): boolean {
  switch (op) {
    case "eq":
      return actual === expected;
    case "neq":
      return actual !== expected;
    case "gt":
      return Number(actual) > Number(expected);
    case "gte":
      return Number(actual) >= Number(expected);
    case "lt":
      return Number(actual) < Number(expected);
    case "lte":
      return Number(actual) <= Number(expected);
    case "contains":
      if (Array.isArray(actual)) return actual.includes(expected);
      return String(actual ?? "").includes(String(expected));
    case "in":
    case "nin": {
      // `expected` es una lista separada por comas ("ES,FR,IT"); trim + descarta vacíos.
      const list = String(expected)
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
      // Lista vacía = condición SIN valores (mal configurada: p. ej. país sin seleccionar). No casa en
      // ningún sentido — ni `in` ni `nin` — para NO exponer al 100% del tráfico con un `nin` vacío.
      if (list.length === 0) return false;
      const a = String(actual ?? "");
      // Valor DESCONOCIDO ('' — p. ej. país no resuelto: VPN/IP interna/geo aún sin veredicto): una
      // condición de conjunto NO se puede afirmar → no casa NI `in` NI `nin`. Decisión de producto:
      // sin país conocido, una condición de país "no hace nada" (ni aplica reglas ni expone tests). Es
      // lo que evita la contaminación A/B en el timeout (evaluar `nin` con '' expondría al cohorte
      // equivocado, sesgado por la velocidad de red).
      if (a === "") return false;
      const inSet = list.indexOf(a) >= 0;
      return op === "in" ? inSet : !inSet;
    }
    case "matches":
      try {
        return new RegExp(String(expected)).test(String(actual ?? ""));
      } catch {
        return false;
      }
    default:
      return false;
  }
}

export function evaluate(node: TriggerNode, ctx: SignalContext): boolean {
  if ("all" in node) return node.all.every((n) => evaluate(n, ctx));
  if ("any" in node) return node.any.some((n) => evaluate(n, ctx));
  if ("not" in node) return !evaluate(node.not, ctx);
  // condición hoja
  const cond = node as Condition;
  return compare(readSignal(ctx, cond), cond.op, cond.value);
}

/**
 * ¿El árbol de condiciones referencia esta señal en alguna hoja? (sin evaluar valores). Recorre la
 * MISMA estructura all/any/not/hoja que `evaluate`, en UN solo sitio, para no duplicar el walker: si
 * mañana TriggerNode gana un tipo de nodo, se actualiza aquí y tanto evaluate como esto lo ven.
 * Lo usa el back-guard para decidir si una regla/experimento/bandit usa la señal de "Atrás".
 */
export function triggerRefsSignal(node: TriggerNode, signal: string): boolean {
  if ("all" in node) return node.all.some((n) => triggerRefsSignal(n, signal));
  if ("any" in node) return node.any.some((n) => triggerRefsSignal(n, signal));
  if ("not" in node) return triggerRefsSignal(node.not, signal);
  return (node as Condition).signal === signal;
}

/** ¿El trigger usa la señal de "Atrás"? Decide si ARMAR el centinela del back-guard — específico de
 *  Atrás, la única señal con centinela de historial. (El nombre de la señal vive aquí, en un sitio). */
export function triggerUsesBackIntent(node: TriggerNode): boolean {
  return triggerRefsSignal(node, "backExitIntent");
}

/**
 * Señales TRANSITORIAS: intención de salida momentánea (no contenido persistente). Un popup disparado
 * por una de ellas es EFÍMERO → se desmonta al navegar (no debe quedarse pegado sobre la página a la
 * que se vuelve). Aquí viven las dos exit-intents (ratón/flick = `exitIntent`, botón Atrás =
 * `backExitIntent`); una futura señal de salida se añade SOLO aquí.
 */
const TRANSIENT_SIGNALS = ["exitIntent", "backExitIntent"] as const;

/**
 * ¿Una señal TRANSITORIA fue NECESARIA para que el trigger se cumpla ahora? (= el popup se muestra
 * por intención de salida → es efímero). Se comprueba poniendo la señal a `false`: si entonces el
 * trigger ya NO se cumpliría, esa señal transitoria es la causa. Maneja triggers COMPUESTOS bien: un
 * `any:[exitIntent, scrollDepthPct>=80]` disparado por el scroll (exitIntent=false) NO es efímero, y
 * un trigger que ni referencia la señal transitoria tampoco (flipearla no cambia su evaluación).
 */
export function triggerFiredByTransient(node: TriggerNode | undefined, state: SignalContext): boolean {
  if (!node) return false;
  return TRANSIENT_SIGNALS.some(
    (sig) => state[sig] === true && !evaluate(node, { ...state, [sig]: false })
  );
}
